import React from 'react';
import PropTypes from 'prop-types';
import { Form, Field, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import {
  Accordion,
  Button,
  Checkbox,
  Col,
  FilterAccordionHeader,
  Modal,
  ModalFooter,
  Row,
  Select,
  TextField,
  TextArea,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import LocalizedTextField from '../../common/LocalizedTextField';
import AuthorizedUsersSection from '../../common/permissions/AuthorizedUsersSection';
import {
  dashboardStatusOptions,
  dashboardVisibilityOptions,
  dashboardThemeOptions,
} from '../../../constants/dataOptions';

function DashboardForm({ open, onClose, onSubmit, initialValues, isLoading }) {
  const intl = useIntl();

  const categoryOptions = [
    { value: 'operational', label: intl.formatMessage({ id: 'ui-reports.dashboard.category.operational', defaultMessage: 'Operational' }) },
    { value: 'executive',   label: intl.formatMessage({ id: 'ui-reports.dashboard.category.executive',   defaultMessage: 'Executive' }) },
    { value: 'analytical',  label: intl.formatMessage({ id: 'ui-reports.dashboard.category.analytical',  defaultMessage: 'Analytical' }) },
    { value: 'tactical',    label: intl.formatMessage({ id: 'ui-reports.dashboard.category.tactical',    defaultMessage: 'Tactical' }) },
    { value: 'strategic',   label: intl.formatMessage({ id: 'ui-reports.dashboard.category.strategic',   defaultMessage: 'Strategic' }) },
  ];

  const defaultInitialValues = {
    name: [{ locale: 'en', value: '' }, { locale: 'ar', value: '' }],
    desc: [{ locale: 'en', value: '' }, { locale: 'ar', value: '' }],
    category: 'operational',
    isDefault: false,
    status: 'published',
    visibility: 'public',
    icon: '',
    styling: { theme: 'light' },
    ...initialValues,
  };

  const footer = (handleSubmit, submitting) => (
    <ModalFooter>
      <Button
        buttonStyle="primary"
        onClick={handleSubmit}
        disabled={submitting || isLoading}
        marginBottom0
      >
        <FormattedMessage id="ui-reports.button.save" />
      </Button>
      <Button onClick={onClose} marginBottom0>
        <FormattedMessage id="ui-reports.button.cancel" />
      </Button>
    </ModalFooter>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      label={
        initialValues?.id
          ? <FormattedMessage id="ui-reports.dashboard.form.editTitle" defaultMessage="Edit Dashboard" />
          : <FormattedMessage id="ui-reports.dashboard.form.createTitle" defaultMessage="Create Dashboard" />
      }
      size="medium"
      dismissible
    >
      <Form
        initialValues={defaultInitialValues}
        onSubmit={onSubmit}
        mutators={{ ...arrayMutators }}
      >
        {({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>

            {/* Basic Info */}
            <Accordion
              id="dashboard-form-basic-accordion"
              label={<FormattedMessage id="ui-reports.dashboard.form.accordion.basic" defaultMessage="Basic Info" />}
              separator={false}
              header={FilterAccordionHeader}
            >
              <Row>
                <Col xs={12}>
                  <FieldArray
                    name="name"
                    component={LocalizedTextField}
                    fieldComponent={TextField}
                    fieldLabel={<FormattedMessage id="ui-reports.dashboard.form.name" defaultMessage="Name" />}
                    isRequired
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <FieldArray
                    name="desc"
                    component={LocalizedTextField}
                    fieldComponent={TextArea}
                    fieldLabel={<FormattedMessage id="ui-reports.dashboard.form.desc" defaultMessage="Description" />}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <Field
                    name="category"
                    component={Select}
                    label={<FormattedMessage id="ui-reports.dashboard.form.category" defaultMessage="Category" />}
                    dataOptions={categoryOptions}
                  />
                </Col>
                <Col xs={3}>
                  <Field
                    name="icon"
                    component={TextField}
                    label={<FormattedMessage id="ui-reports.dashboard.form.icon" defaultMessage="Icon" />}
                  />
                </Col>
                <Col xs={3}>
                  <Field
                    name="isDefault"
                    component={Checkbox}
                    type="checkbox"
                    label={<FormattedMessage id="ui-reports.dashboard.form.isDefault" defaultMessage="Default" />}
                  />
                </Col>
              </Row>
            </Accordion>

            {/* Settings */}
            <Accordion
              id="dashboard-form-settings-accordion"
              label={<FormattedMessage id="ui-reports.dashboard.form.accordion.settings" defaultMessage="Settings" />}
              separator={false}
              header={FilterAccordionHeader}
            >
              <Row>
                <Col xs={6}>
                  <Field
                    name="status"
                    component={Select}
                    label={<FormattedMessage id="ui-reports.dashboard.form.status" defaultMessage="Status" />}
                    dataOptions={dashboardStatusOptions(intl)}
                  />
                </Col>
                <Col xs={6}>
                  <Field
                    name="styling.theme"
                    component={Select}
                    label={<FormattedMessage id="ui-reports.dashboard.form.theme" defaultMessage="Theme" />}
                    dataOptions={dashboardThemeOptions(intl)}
                  />
                </Col>
              </Row>
            </Accordion>

            {/* Permissions */}
            <Accordion
              id="dashboard-form-permissions-accordion"
              label={<FormattedMessage id="ui-reports.dashboard.form.accordion.permissions" defaultMessage="Permissions" />}
              separator={false}
              header={FilterAccordionHeader}
            >
              <Row>
                <Col xs={12}>
                  <Field
                    name="visibility"
                    component={Select}
                    label={<FormattedMessage id="ui-reports.dashboard.form.visibility" defaultMessage="Visibility" />}
                    dataOptions={dashboardVisibilityOptions(intl)}
                  />
                </Col>
              </Row>
              <FormSpy subscription={{ values: true }}>
                {({ values }) => (
                  <AuthorizedUsersSection
                    visibility={values.visibility}
                    restrictedValue="shared"
                    accordionId="dashboard-form-authorized-users"
                  />
                )}
              </FormSpy>
            </Accordion>

            {footer(handleSubmit, submitting)}
          </form>
        )}
      </Form>
    </Modal>
  );
}

DashboardForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default DashboardForm;
